import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  CalendarCheck,
  ChevronDown,
  Clock,
  Upload,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileSpreadsheet,
  Info,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

const ATTENDANCE_STATUSES = [
  { code: "P", label: "Present", desc: "Employee was present and worked during the scheduled hours.", tint: "ic-green" },
  { code: "A", label: "Absent", desc: "Employee did not attend work and no leave was applied.", tint: "ic-rose" },
  { code: "HD", label: "Half Day", desc: "Employee was present for half the scheduled working hours.", tint: "ic-amber" },
  { code: "L", label: "Leave", desc: "Employee is on approved leave (paid or unpaid).", tint: "ic-teal" },
  { code: "WO", label: "Week Off", desc: "Scheduled weekly off day for the employee.", tint: "ic-slate" },
  { code: "H", label: "Holiday", desc: "Organisation-declared holiday.", tint: "ic-blue" },
  { code: "WFH", label: "Work From Home", desc: "Employee worked remotely. Counted as present for payroll.", tint: "ic-green" },
  { code: "OD", label: "On Duty", desc: "Employee was on official duty outside the office. Counted as present for payroll.", tint: "ic-green" },
];

const WORKFLOW_STEPS = [
  { num: 1, label: "Configure Attendance", desc: "Set up attendance method and work schedule in Settings." },
  { num: 2, label: "Mark or Import", desc: "Mark attendance daily or import via Excel/CSV upload." },
  { num: 3, label: "Review Records", desc: "View and verify attendance records in the calendar or table view." },
  { num: 4, label: "Payroll Picks Up", desc: "Payroll uses attendance data to compute payable days." },
];

const FAQ_DATA = [
  {
    q: "What is the Attendance module?",
    a: "The Attendance module in BrixiGo lets you track employee working days, mark daily attendance, import bulk attendance from spreadsheets, and view attendance summaries. It feeds directly into payroll for calculating payable days.",
  },
  {
    q: "How do I mark attendance for an employee?",
    a: "Go to Attendance and use the Daily Attendance view. Select the employee and date, choose the status (Present, Absent, Half Day, Leave, etc.), and save. You can also mark attendance for multiple employees at once.",
  },
  {
    q: "How do I import attendance from a spreadsheet?",
    a: "Go to Attendance > Import. Download the template file (.xlsx), fill in employee codes, dates, and statuses, then upload the completed file. The system validates your data and shows any errors before importing.",
  },
  {
    q: "What file formats are supported for attendance import?",
    a: "BrixiGo supports .csv and .xlsx files for attendance import. The file must contain columns for employee_code, work_date, and status. Optional columns include work_hours, check_in, check_out, shift, and remarks.",
  },
  {
    q: "How does attendance affect payroll?",
    a: "Payroll uses attendance data to compute payable days for each employee. Present and Half Day statuses count toward payable days. The pay period attendance summary is pulled automatically when you process payroll.",
  },
  {
    q: "What does 'Apply Calendar' do?",
    a: "Apply Calendar automatically marks holidays and week-offs for all employees based on your configured holiday calendar and weekly off settings. This saves time compared to marking these individually.",
  },
  {
    q: "Can employees view their own attendance?",
    a: "Yes. Employees can view their own attendance records through the My Attendance section, which shows their attendance history and summary.",
  },
  {
    q: "What happens if attendance is not marked for an employee?",
    a: "If no attendance record exists for a work date, the system may treat it as absent depending on your payroll configuration. It is recommended to ensure all attendance is marked before processing payroll.",
  },
];

const ERROR_SOLUTIONS = [
  {
    error: "employee_code column missing",
    cause: "The uploaded file does not contain a recognisable employee_code column.",
    fix: "Ensure your spreadsheet has a column named 'employee_code' (or 'emp_code', 'code', 'employee_id'). Download the template for the correct format.",
  },
  {
    error: "employee_code is empty",
    cause: "One or more rows have a blank employee_code value.",
    fix: "Check your spreadsheet for empty rows or missing employee codes. Every row must have a valid employee code.",
  },
  {
    error: "Unknown employee_code (not in your organisation)",
    cause: "The employee code in the file does not match any employee in your BrixiGo organisation.",
    fix: "Verify the employee code is correct and that the employee exists in BrixiGo. Check for typos or extra spaces.",
  },
  {
    error: "Invalid work_date",
    cause: "The date value could not be parsed into a valid date.",
    fix: "Use the format YYYY-MM-DD (e.g. 2026-03-01). Other accepted formats include DD/MM/YYYY and MM/DD/YYYY.",
  },
  {
    error: "Only .csv and .xlsx files are supported",
    cause: "You uploaded a file that is not a CSV or Excel file.",
    fix: "Save your file as .csv or .xlsx before uploading. Other formats like .xls, .pdf, or .txt are not supported.",
  },
  {
    error: "No attendance rows were saved",
    cause: "The bulk upload processed but no records were successfully saved due to validation errors.",
    fix: "Review the error details returned by the system. Fix the issues in your spreadsheet and re-upload.",
  },
  {
    error: "Failed to fetch pay period attendance summary",
    cause: "Payroll could not retrieve attendance data for the selected pay period.",
    fix: "Ensure attendance records exist for the pay period dates. Check that attendance has been marked or imported before running payroll.",
  },
];

export default function AttendanceHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const filteredFaqs = search.trim()
    ? FAQ_DATA.filter(
        (f) =>
          f.q.toLowerCase().includes(search.toLowerCase()) ||
          f.a.toLowerCase().includes(search.toLowerCase())
      )
    : FAQ_DATA;

  const filteredErrors = search.trim()
    ? ERROR_SOLUTIONS.filter(
        (e) =>
          e.error.toLowerCase().includes(search.toLowerCase()) ||
          e.cause.toLowerCase().includes(search.toLowerCase()) ||
          e.fix.toLowerCase().includes(search.toLowerCase())
      )
    : ERROR_SOLUTIONS;

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-green">
              <CalendarCheck size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Attendance</h1>
              <p className="hs-hero-sub">
                Understand how to track, import, and manage employee attendance in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. shift, biometric, weekly off..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* Overview Section */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Attendance Overview</h2>
        </div>
        <div className="ah-content-card">
          <p>
            The Attendance module in BrixiGo allows you to track and manage employee attendance
            records. You can mark daily attendance, import attendance data in bulk via
            spreadsheet uploads, view attendance calendars, and review attendance summaries.
          </p>
          <p>
            Attendance data is used by the Payroll module to calculate payable days for each
            employee during a pay period.
          </p>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <CalendarCheck size={18} className="ic-green" />
              <span>Daily attendance marking</span>
            </div>
            <div className="ah-feature-item">
              <Upload size={18} className="ic-blue" />
              <span>Bulk import via Excel/CSV</span>
            </div>
            <div className="ah-feature-item">
              <Clock size={18} className="ic-amber" />
              <span>Work hours tracking</span>
            </div>
            <div className="ah-feature-item">
              <FileSpreadsheet size={18} className="ic-teal" />
              <span>Monthly calendar view</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Clock size={20} className="ah-section-icon ic-amber" />
          <h2 className="hs-section-title">How Attendance Works</h2>
        </div>
        <div className="ah-content-card">
          <div className="ah-workflow-steps">
            {WORKFLOW_STEPS.map((step, idx) => (
              <div key={step.num} className="ah-workflow-step">
                <div className="ah-workflow-num">{step.num}</div>
                <div className="ah-workflow-body">
                  <div className="ah-workflow-label">{step.label}</div>
                  <div className="ah-workflow-desc">{step.desc}</div>
                </div>
                {idx < WORKFLOW_STEPS.length - 1 && <div className="ah-workflow-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attendance & Payroll */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CheckCircle2 size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Attendance & Payroll</h2>
        </div>
        <div className="ah-content-card">
          <p>
            When you process payroll, BrixiGo pulls the attendance summary for the selected
            pay period. This determines how many payable days each employee has.
          </p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Present</strong> and <strong>Half Day</strong> count toward payable days</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>WFH</strong> and <strong>On Duty</strong> are treated as present</span>
            </div>
            <div className="ah-payroll-item ah-payroll-no">
              <XCircle size={16} />
              <span><strong>Absent</strong> days may reduce payable days (based on payroll configuration)</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span>Ensure attendance is marked before processing payroll to avoid discrepancies</span>
            </div>
          </div>
        </div>
      </section>

      {/* Attendance Statuses */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CalendarCheck size={20} className="ah-section-icon ic-teal" />
          <h2 className="hs-section-title">Attendance Statuses</h2>
        </div>
        <div className="ah-content-card">
          <p>BrixiGo uses the following attendance statuses:</p>
          <div className="ah-status-grid">
            {ATTENDANCE_STATUSES.map((s) => (
              <div key={s.code} className="ah-status-item">
                <div className="ah-status-header">
                  <span className={`ah-status-code ${s.tint}`}>{s.code}</span>
                  <span className="ah-status-label">{s.label}</span>
                </div>
                <div className="ah-status-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="ah-section" ref={faqRef}>
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="ah-content-card">
          <div className="hs-faq-list">
            {filteredFaqs.length === 0 && (
              <p className="ah-empty">No matching questions found.</p>
            )}
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{faq.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Errors */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Common Errors & Solutions</h2>
        </div>
        <div className="ah-content-card">
          {filteredErrors.length === 0 && (
            <p className="ah-empty">No matching errors found.</p>
          )}
          <div className="ah-errors-list">
            {filteredErrors.map((item) => (
              <div key={item.error} className="ah-error-item">
                <div className="ah-error-title">
                  <AlertTriangle size={14} />
                  <code>{item.error}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{item.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
